import {
  Discount,
  PlanStage,
} from "@app/core/store/plan-editor/plan/plan.model";

export class PlanCalculations {
  static calculateStageTotals(stage: PlanStage): {
    total: number;
    priceRangeMin: number;
    priceRangeMax: number;
    hasOptionalItems: boolean;
    hasDiscount: boolean;
    totalWithDiscount: number;
    priceRangeMinWithDiscount: number;
    priceRangeMaxWithDiscount: number;
  } {
    const total = stage.items.reduce((sum, item) => sum + (item.price || 0), 0);
    const priceRangeMin = stage.items
      .filter((item) => !item.optional)
      .reduce((sum, item) => sum + (item.price || 0), 0);
    const priceRangeMax = total;
    const hasOptionalItems = stage.items.some((item) => item.optional);

    let hasDiscount = !!stage.discount;
    let totalWithDiscount = total;
    let priceRangeMinWithDiscount = priceRangeMin;
    let priceRangeMaxWithDiscount = priceRangeMax;

    if (hasDiscount) {
      if (stage.discount.type === "percentage") {
        totalWithDiscount = Math.max(
          total - (total * stage.discount.value) / 100,
          0,
        );
        priceRangeMinWithDiscount = Math.max(
          priceRangeMin - (priceRangeMin * stage.discount.value) / 100,
          0,
        );
        priceRangeMaxWithDiscount = Math.max(
          priceRangeMax - (priceRangeMax * stage.discount.value) / 100,
          0,
        );
      } else {
        totalWithDiscount = Math.max(total - stage.discount.value, 0);
        priceRangeMinWithDiscount = Math.max(
          priceRangeMin - stage.discount.value,
          0,
        );
        priceRangeMaxWithDiscount = Math.max(
          priceRangeMax - stage.discount.value,
          0,
        );
      }
    }

    return {
      total,
      priceRangeMin,
      priceRangeMax,
      hasOptionalItems,
      hasDiscount,
      totalWithDiscount,
      priceRangeMinWithDiscount,
      priceRangeMaxWithDiscount,
    };
  }

  static calculatePlanTotals(
    stages: PlanStage[],
    discount: Discount,
  ): {
    total: number;
    priceRangeMin: number;
    priceRangeMax: number;
    hasOptionalItems: boolean;
    hasDiscount: boolean;
    hasPlanDiscount: boolean;
    totalWithDiscount: number;
    priceRangeMinWithDiscount: number;
    priceRangeMaxWithDiscount: number;
    hasStageDiscounts: boolean;
  } {
    const stageTotals = stages.map((stage) => this.calculateStageTotals(stage));

    const total = stageTotals.reduce((sum, stage) => sum + stage.total, 0);
    const priceRangeMin = stageTotals.reduce(
      (sum, stage) => sum + stage.priceRangeMin,
      0,
    );
    const priceRangeMax = stageTotals.reduce(
      (sum, stage) => sum + stage.priceRangeMax,
      0,
    );
    const hasStageDiscounts = stageTotals.some((stage) => stage.hasDiscount);
    const totalAfterStageDiscounts = stageTotals.reduce(
      (sum, stage) => sum + stage.totalWithDiscount,
      0,
    );
    const priceRangeMinAfterStageDiscounts = stageTotals.reduce(
      (sum, stage) => sum + stage.priceRangeMinWithDiscount,
      0,
    );
    const priceRangeMaxAfterStageDiscounts = stageTotals.reduce(
      (sum, stage) => sum + stage.priceRangeMaxWithDiscount,
      0,
    );
    const hasPlanDiscount = !!discount;
    let totalWithDiscount = totalAfterStageDiscounts;
    let priceRangeMinWithDiscount = priceRangeMinAfterStageDiscounts;
    let priceRangeMaxWithDiscount = priceRangeMaxAfterStageDiscounts;

    if (hasPlanDiscount) {
      if (discount.type === "percentage") {
        totalWithDiscount = Math.max(
          totalAfterStageDiscounts -
            (totalAfterStageDiscounts * discount.value) / 100,
          0,
        );
        priceRangeMinWithDiscount = Math.max(
          priceRangeMinAfterStageDiscounts -
            (priceRangeMinAfterStageDiscounts * discount.value) / 100,
          0,
        );
        priceRangeMaxWithDiscount = Math.max(
          priceRangeMaxAfterStageDiscounts -
            (priceRangeMaxAfterStageDiscounts * discount.value) / 100,
          0,
        );
      } else {
        totalWithDiscount = Math.max(
          totalAfterStageDiscounts - discount.value,
          0,
        );
        priceRangeMinWithDiscount = Math.max(
          priceRangeMinAfterStageDiscounts - discount.value,
          0,
        );
        priceRangeMaxWithDiscount = Math.max(
          priceRangeMaxAfterStageDiscounts - discount.value,
          0,
        );
      }
    }

    const hasDiscount = hasStageDiscounts || hasPlanDiscount;
    const hasOptionalItems = stageTotals.some(
      (stage) => stage.hasOptionalItems,
    );

    return {
      total,
      priceRangeMin,
      priceRangeMax,
      hasOptionalItems,
      hasDiscount,
      hasPlanDiscount,
      totalWithDiscount,
      priceRangeMinWithDiscount,
      priceRangeMaxWithDiscount,
      hasStageDiscounts,
    };
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/utils/plan-calculations.ts